/**
 * The months a sales source will put on the grid, before it is saved.
 *
 * The create dialog asks for doors, a per-store rate, growth and a price, and the planner had to
 * save to see what they added up to. This folds the generator's own output (the same rows the
 * grid will get) into one line per period, so the dialog can show the plan as it is typed.
 */

import type { TimePeriod } from "../types"
import type { CreateSalesSourceFormData } from "../components/create_sales_source_dialog"
import { generateSalesSourceData, type SalesSourcePlanContext } from "./data_generator"

export interface PreviewPeriod {
  period_key: string
  stores: number
  cases: number
  dollars: number
}

export interface SalesSourcePreview {
  periods: PreviewPeriod[]
  total_cases: number
  total_dollars: number
  /** Doors in the first period that carries volume. */
  opening_stores: number
  /** Doors in the last period that carries volume. */
  closing_stores: number
}

interface PreviewNode {
  children?: PreviewNode[]
  sales_data?: Array<{ period?: { period_key?: string }; metrics?: { store_count?: number; cases_sold?: number; volume_dollars?: number } }>
}

/** Sum every leaf row's periods. Leaves only: parents are empty until the grid aggregates them. */
export function fold_preview_rows(roots: PreviewNode[], periods: TimePeriod[]): SalesSourcePreview {
  const by_key = new Map<string, PreviewPeriod>()
  for (const period of periods) {
    by_key.set(period.period_key, { period_key: period.period_key, stores: 0, cases: 0, dollars: 0 })
  }
  function walk(node: PreviewNode): void {
    const children = node.children ?? []
    if (children.length > 0) {
      children.forEach(walk)
      return
    }
    for (const point of node.sales_data ?? []) {
      const key = point.period?.period_key
      if (!key) continue
      const row = by_key.get(key)
      if (!row) continue
      row.stores += Number(point.metrics?.store_count ?? 0)
      row.cases += Number(point.metrics?.cases_sold ?? 0)
      row.dollars += Number(point.metrics?.volume_dollars ?? 0)
    }
  }
  roots.forEach(walk)
  const rows = periods.map((period: TimePeriod) => by_key.get(period.period_key)!)
  const with_volume = rows.filter((row: PreviewPeriod) => row.dollars > 0 || row.cases > 0)
  return {
    periods: rows,
    total_cases: rows.reduce((sum: number, row: PreviewPeriod) => sum + row.cases, 0),
    // Cents, so twelve rounded months add up to the figure the grid's footer will show.
    total_dollars: rows.reduce((sum: number, row: PreviewPeriod) => sum + Math.round(row.dollars * 100), 0) / 100,
    opening_stores: with_volume[0]?.stores ?? 0,
    closing_stores: with_volume[with_volume.length - 1]?.stores ?? 0,
  }
}

/**
 * What the form would generate right now, or `null` while it cannot generate anything (no
 * customer, no complete growth row). Never throws: the preview is advisory and the form's own
 * validation still runs on save.
 */
export function preview_sales_source(
  formData: CreateSalesSourceFormData,
  periods: TimePeriod[],
  plan?: SalesSourcePlanContext
): SalesSourcePreview | null {
  if (!formData.customer.trim() || periods.length === 0) return null
  const ready = formData.growthExpectations.filter(
    (exp) => exp.initialDoors > 0 && exp.initialCasesPerMonth > 0 && exp.costPerCase > 0 && Boolean(exp.startMonth)
  )
  if (ready.length === 0) return null
  try {
    const { nodes } = generateSalesSourceData({ ...formData, growthExpectations: ready }, periods, plan)
    return fold_preview_rows(nodes as PreviewNode[], periods)
  } catch {
    return null
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function period(key: string): TimePeriod {
    const [year, month] = key.split("-").map(Number)
    return { period_key: key, year, month, label: key, grain: "month" } as unknown as TimePeriod
  }
  const periods = ["2027-01", "2027-02", "2027-03"].map(period)

  describe("fold_preview_rows", () => {
    it("adds leaf rows per period and reads opening and closing doors off the months with volume", () => {
      const roots: PreviewNode[] = [
        {
          children: [
            {
              sales_data: [
                { period: { period_key: "2027-01" }, metrics: { store_count: 100, cases_sold: 100, volume_dollars: 1000 } },
                { period: { period_key: "2027-02" }, metrics: { store_count: 110, cases_sold: 110, volume_dollars: 1100 } },
                { period: { period_key: "2027-03" }, metrics: { store_count: 0, cases_sold: 0, volume_dollars: 0 } },
              ],
            },
            {
              sales_data: [{ period: { period_key: "2027-01" }, metrics: { store_count: 100, cases_sold: 50, volume_dollars: 500.5 } }],
            },
          ],
        },
      ]
      const preview = fold_preview_rows(roots, periods)
      expect(preview.periods.map((row: PreviewPeriod) => row.dollars)).toEqual([1500.5, 1100, 0])
      expect(preview.total_cases).toBe(260)
      expect(preview.total_dollars).toBe(2600.5)
      expect(preview.opening_stores).toBe(200)
      expect(preview.closing_stores).toBe(110)
    })
  })

  describe("preview_sales_source", () => {
    function form(extra: Partial<CreateSalesSourceFormData> = {}): CreateSalesSourceFormData {
      return {
        classOfTrade: "",
        customer: "Kroger",
        routeToMarket: "",
        customerIsOther: false,
        planningBySku: false,
        isOpportunity: false,
        opportunityName: "",
        opportunityLikelihood: 50,
        growthExpectations: [
          {
            id: "g1",
            name: "",
            initialDoors: 100,
            startMonth: "2027-01",
            storeCountGrowthMultiplier: 0,
            productLineName: "BCAA's",
            initialCasesPerMonth: 1,
            costPerCase: 10,
            sameStoreSalesMultiplier: 0,
          },
        ],
        ...extra,
      }
    }

    it("shows 100 doors at 1 case a month as 100 cases and $1,000 a month on a total-basis plan", () => {
      const preview = preview_sales_source(form(), periods, { quantity_basis: "total" })
      expect(preview?.periods.map((row: PreviewPeriod) => [row.stores, row.cases, row.dollars])).toEqual([
        [100, 100, 1000],
        [100, 100, 1000],
        [100, 100, 1000],
      ])
      expect(preview?.total_dollars).toBe(3000)
    })

    it("is nothing until the form can generate a row", () => {
      expect(preview_sales_source(form({ customer: "" }), periods)).toBeNull()
      expect(preview_sales_source(form({ growthExpectations: [] }), periods)).toBeNull()
      const incomplete = form()
      incomplete.growthExpectations[0].costPerCase = 0
      expect(preview_sales_source(incomplete, periods)).toBeNull()
    })
  })
}
